import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';
import { RFQ, Quotation, RFQFormData, QuotationFormData } from '@/types/quotations';
import { notifyProcurement } from '@/lib/notifications/notificationService';
import { TEMPLATE_RFQ_SENT, TEMPLATE_QUOTATION_APPROVED } from '@/lib/notifications/templateCodes';

interface QuotationState {
  rfqs: RFQ[];
  quotations: Quotation[];
  isLoading: boolean;
  error: string | null;
  
  // RFQ Actions
  fetchRFQs: () => Promise<void>;
  createRFQ: (data: RFQFormData) => Promise<void>;
  updateRFQ: (id: string, data: Partial<RFQFormData>) => Promise<void>;
  deleteRFQ: (id: string) => Promise<void>;
  sendRFQ: (id: string) => Promise<void>;
  closeRFQ: (id: string) => Promise<void>;
  
  // Quotation Actions
  fetchQuotations: (rfqId?: string) => Promise<void>;
  createQuotation: (data: QuotationFormData) => Promise<void>;
  updateQuotation: (id: string, data: Partial<QuotationFormData>) => Promise<void>;
  deleteQuotation: (id: string) => Promise<void>;
  approveQuotation: (id: string) => Promise<void>;
  rejectQuotation: (id: string, reason: string) => Promise<void>;
}

export const useQuotationStore = create<QuotationState>((set, get) => ({
  rfqs: [],
  quotations: [],
  isLoading: false,
  error: null,

  fetchRFQs: async () => {
    try {
      set({ isLoading: true, error: null });
      const { data, error } = await supabase
        .from('rfqs')
        .select(`
          *,
          pr:pr_id (
            id,
            code
          ),
          suppliers:rfq_suppliers (
            id,
            supplier:supplier_id (
              id,
              name
            ),
            status,
            sent_at,
            response_date
          ),
          items:rfq_items (*)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ rfqs: data || [] });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch RFQs';
      set({ error: message });
    } finally {
      set({ isLoading: false });
    }
  },

  createRFQ: async (data) => {
    try {
      set({ isLoading: true, error: null });

      // Generate RFQ number (RFQ-YYMM-XXXX format)
      const now = new Date();
      const yearMonth = `${now.getFullYear().toString().slice(-2)}${(now.getMonth() + 1).toString().padStart(2, '0')}`;
      
      // Get the latest RFQ number for this month (use maybeSingle to handle no results)
      const { data: latestRFQs } = await supabase
        .from('rfqs')
        .select('rfq_number')
        .like('rfq_number', `RFQ-${yearMonth}-%`)
        .order('rfq_number', { ascending: false })
        .limit(1);
      
      let sequence = 1;
      if (latestRFQs && latestRFQs.length > 0 && latestRFQs[0].rfq_number) {
        const lastSeq = parseInt(latestRFQs[0].rfq_number.split('-')[2], 10);
        if (!isNaN(lastSeq)) sequence = lastSeq + 1;
      }
      const rfqNumber = `RFQ-${yearMonth}-${sequence.toString().padStart(4, '0')}`;

      // First create the RFQ
      const { data: rfq, error: rfqError } = await supabase
        .from('rfqs')
        .insert([{
          rfq_number: rfqNumber,
          pr_id: data.pr_id,
          required_date: data.required_date,
          terms_conditions: data.terms_conditions,
          notes: data.notes,
          status: 'draft',
          created_by: (await supabase.auth.getUser()).data.user?.id
        }])
        .select()
        .single();

      if (rfqError) throw rfqError;

      // Create RFQ suppliers
      const rfqSuppliers = data.supplier_ids.map(supplierId => ({
        rfq_id: rfq.id,
        supplier_id: supplierId,
        status: 'pending'
      }));

      const { error: suppliersError } = await supabase
        .from('rfq_suppliers')
        .insert(rfqSuppliers);

      if (suppliersError) throw suppliersError;

      // Create RFQ items - ensure pr_item_id is a valid UUID or null
      const rfqItems = data.items.map(item => ({
        rfq_id: rfq.id,
        pr_item_id: item.pr_item_id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(item.pr_item_id) ? item.pr_item_id : null,
        item_code: item.item_code,
        item_name: item.item_name,
        description: item.description || null,
        quantity: item.quantity,
        unit: item.unit,
        specifications: item.specifications || null
      }));

      const { error: itemsError } = await supabase
        .from('rfq_items')
        .insert(rfqItems);

      if (itemsError) throw itemsError;

      await get().fetchRFQs();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create RFQ';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  updateRFQ: async (id, data) => {
    try {
      set({ isLoading: true, error: null });

      const { error: rfqError } = await supabase
        .from('rfqs')
        .update({
          required_date: data.required_date,
          terms_conditions: data.terms_conditions,
          notes: data.notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (rfqError) throw rfqError;

      if (data.supplier_ids) {
        // Delete existing suppliers
        const { error: deleteError } = await supabase
          .from('rfq_suppliers')
          .delete()
          .eq('rfq_id', id);

        if (deleteError) throw deleteError;

        // Add new suppliers
        const rfqSuppliers = data.supplier_ids.map(supplierId => ({
          rfq_id: id,
          supplier_id: supplierId,
          status: 'pending'
        }));

        const { error: suppliersError } = await supabase
          .from('rfq_suppliers')
          .insert(rfqSuppliers);

        if (suppliersError) throw suppliersError;
      }

      if (data.items) {
        // Delete existing items
        const { error: deleteError } = await supabase
          .from('rfq_items')
          .delete()
          .eq('rfq_id', id);

        if (deleteError) throw deleteError;

        // Add new items
        const rfqItems = data.items.map(item => ({
          rfq_id: id,
          ...item
        }));

        const { error: itemsError } = await supabase
          .from('rfq_items')
          .insert(rfqItems);

        if (itemsError) throw itemsError;
      }

      await get().fetchRFQs();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update RFQ';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteRFQ: async (id) => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase
        .from('rfqs')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await get().fetchRFQs();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete RFQ';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  sendRFQ: async (id) => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase
        .from('rfqs')
        .update({ 
          status: 'sent',
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      // Update supplier statuses
      const { error: supplierError } = await supabase
        .from('rfq_suppliers')
        .update({ 
          status: 'sent',
          sent_at: new Date().toISOString()
        })
        .eq('rfq_id', id);

      if (supplierError) throw supplierError;

      // Queue email notification for RFQ sent
      const rfq = get().rfqs.find(r => r.id === id);
      notifyProcurement(
        TEMPLATE_RFQ_SENT,
        {
          rfq_number: rfq?.rfq_number || id,
          supplier_names: rfq?.suppliers?.map((s: any) => s.supplier?.name).filter(Boolean).join(', ') || 'N/A',
          items_count: rfq?.items?.length || 0,
          required_date: rfq?.required_date || 'N/A',
        },
        'rfqs',
        id
      );

      await get().fetchRFQs();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send RFQ';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  closeRFQ: async (id) => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase
        .from('rfqs')
        .update({ 
          status: 'closed',
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;
      await get().fetchRFQs();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to close RFQ';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  fetchQuotations: async (rfqId?: string) => {
    try {
      set({ isLoading: true, error: null });
      let query = supabase
        .from('quotations')
        .select(`
          *,
          rfq:rfq_id (
            id,
            rfq_number,
            pr:pr_id (
              id,
              code
            )
          ),
          supplier:supplier_id (
            id,
            name
          ),
          items:quotation_items (*)
        `)
        .order('created_at', { ascending: false });

      if (rfqId) {
        query = query.eq('rfq_id', rfqId);
      }

      const { data, error } = await query;

      if (error) throw error;
      set({ quotations: data || [] });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch quotations';
      set({ error: message });
    } finally {
      set({ isLoading: false });
    }
  },

  createQuotation: async (data) => {
    try {
      set({ isLoading: true, error: null });

      // Generate quotation number (QUO-YYMM-XXXX format)
      const now = new Date();
      const yearMonth = `${now.getFullYear().toString().slice(-2)}${(now.getMonth() + 1).toString().padStart(2, '0')}`;
      
      const { data: latestQuotations } = await supabase
        .from('quotations')
        .select('quotation_number')
        .like('quotation_number', `QUO-${yearMonth}-%`)
        .order('quotation_number', { ascending: false })
        .limit(1);
      
      let sequence = 1;
      if (latestQuotations && latestQuotations.length > 0 && latestQuotations[0].quotation_number) {
        const lastSeq = parseInt(latestQuotations[0].quotation_number.split('-')[2], 10);
        if (!isNaN(lastSeq)) sequence = lastSeq + 1;
      }
      const quotationNumber = `QUO-${yearMonth}-${sequence.toString().padStart(4, '0')}`;

      // Calculate totals
      let subtotal = 0;
      let taxAmount = 0;

      const quotationItems = data.items.map(item => {
        const itemTotal = item.quantity * item.unit_price;
        const itemTax = item.tax_rate ? (itemTotal * item.tax_rate / 100) : 0;
        
        subtotal += itemTotal;
        taxAmount += itemTax;

        return {
          ...item,
          tax_amount: itemTax,
          total_amount: itemTotal + itemTax
        };
      });

      // Create quotation
      const { data: quotation, error: quotationError } = await supabase
        .from('quotations')
        .insert([{
          quotation_number: quotationNumber,
          rfq_id: data.rfq_id,
          supplier_id: data.supplier_id,
          valid_until: data.valid_until,
          currency: data.currency,
          exchange_rate: data.exchange_rate,
          subtotal,
          tax_amount: taxAmount,
          total_amount: subtotal + taxAmount,
          payment_terms: data.payment_terms,
          delivery_terms: data.delivery_terms,
          delivery_time: data.delivery_time,
          warranty_terms: data.warranty_terms,
          notes: data.notes,
          status: 'draft'
        }])
        .select()
        .single();

      if (quotationError) throw quotationError;

      // Create quotation items - ensure rfq_item_id is a valid UUID or null
      const { error: itemsError } = await supabase
        .from('quotation_items')
        .insert(quotationItems.map(item => ({
          rfq_item_id: item.rfq_item_id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(item.rfq_item_id) ? item.rfq_item_id : null,
          item_code: item.item_code,
          item_name: item.item_name || '',
          description: item.description || null,
          quantity: item.quantity,
          unit: item.unit,
          unit_price: item.unit_price,
          tax_rate: item.tax_rate || 0,
          tax_amount: item.tax_amount,
          total_amount: item.total_amount,
          delivery_date: item.delivery_date || null,
          notes: item.notes || null,
          quotation_id: quotation.id
        })));

      if (itemsError) throw itemsError;

      await get().fetchQuotations();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create quotation';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  updateQuotation: async (id, data) => {
    try {
      set({ isLoading: true, error: null });

      // Extract items from data to handle separately
      const { items, ...quotationData } = data;

      if (items && items.length > 0) {
        // Calculate totals
        let subtotal = 0;
        let taxAmount = 0;

        const quotationItems = items.map(item => {
          const itemTotal = (item.quantity || 0) * (item.unit_price || 0);
          const itemTax = item.tax_rate ? (itemTotal * item.tax_rate / 100) : 0;
          
          subtotal += itemTotal;
          taxAmount += itemTax;

          return {
            rfq_item_id: item.rfq_item_id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(item.rfq_item_id) ? item.rfq_item_id : null,
            item_code: item.item_code || '',
            item_name: item.item_name || '',
            description: item.description || null,
            quantity: item.quantity || 0,
            unit: item.unit || '',
            unit_price: item.unit_price || 0,
            tax_rate: item.tax_rate || 0,
            tax_amount: itemTax,
            total_amount: itemTotal + itemTax,
            delivery_date: item.delivery_date || null,
            notes: item.notes || null
          };
        });

        // Update quotation (without items field)
        const { error: quotationError } = await supabase
          .from('quotations')
          .update({
            valid_until: quotationData.valid_until,
            currency: quotationData.currency || 'INR',
            exchange_rate: quotationData.exchange_rate,
            payment_terms: quotationData.payment_terms,
            delivery_terms: quotationData.delivery_terms,
            delivery_time: quotationData.delivery_time,
            warranty_terms: quotationData.warranty_terms,
            notes: quotationData.notes,
            subtotal,
            tax_amount: taxAmount,
            total_amount: subtotal + taxAmount,
            updated_at: new Date().toISOString()
          })
          .eq('id', id);

        if (quotationError) throw quotationError;

        // Delete existing items
        const { error: deleteError } = await supabase
          .from('quotation_items')
          .delete()
          .eq('quotation_id', id);

        if (deleteError) throw deleteError;

        // Insert new items
        const { error: itemsError } = await supabase
          .from('quotation_items')
          .insert(quotationItems.map(item => ({
            ...item,
            quotation_id: id
          })));

        if (itemsError) throw itemsError;
      } else {
        // Update quotation without items
        const { error: quotationError } = await supabase
          .from('quotations')
          .update({
            valid_until: quotationData.valid_until,
            currency: quotationData.currency,
            exchange_rate: quotationData.exchange_rate,
            payment_terms: quotationData.payment_terms,
            delivery_terms: quotationData.delivery_terms,
            delivery_time: quotationData.delivery_time,
            warranty_terms: quotationData.warranty_terms,
            notes: quotationData.notes,
            updated_at: new Date().toISOString()
          })
          .eq('id', id);

        if (quotationError) throw quotationError;
      }

      await get().fetchQuotations();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update quotation';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteQuotation: async (id) => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase
        .from('quotations')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await get().fetchQuotations();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete quotation';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  approveQuotation: async (id) => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase
        .from('quotations')
        .update({ 
          status: 'approved',
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      // Queue email notification for quotation approval
      const quotation = get().quotations.find(q => q.id === id);
      notifyProcurement(
        TEMPLATE_QUOTATION_APPROVED,
        {
          quotation_number: quotation?.quotation_number || id,
          supplier: (quotation as any)?.supplier?.name || 'N/A',
          total_amount: quotation?.total_amount || 0,
        },
        'quotations',
        id
      );

      await get().fetchQuotations();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to approve quotation';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  rejectQuotation: async (id, reason) => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase
        .from('quotations')
        .update({ 
          status: 'rejected',
          notes: reason,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;
      await get().fetchQuotations();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reject quotation';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  }
}));

// Set up realtime subscription
supabase
  .channel('quotation_changes')
  .on(
    'postgres_changes',
    { event: '*', schema: 'public', table: 'rfqs' },
    () => {
      useQuotationStore.getState().fetchRFQs();
    }
  )
  .on(
    'postgres_changes',
    { event: '*', schema: 'public', table: 'quotations' },
    () => {
      useQuotationStore.getState().fetchQuotations();
    }
  )
  .subscribe();